<template>
  <!-- editable: click the annotation to edit its AI fields (per `fields` prop) -->
  <fts-dropdown-select
    v-if="editable && hasContent"
    placement="bottom-start"
    strategy-fixed
    prevent-overflow-tether-off
    :prevent-overflow-boundary="boundary"
    :offset="[0, 4]"
    :z-index="10"
    stop-propagation
    content-class="fts-ai-annotation-edit-content"
    class="fts-ai-annotation-editable stopPropagation"
    @show="initDraft"
  >
    <!-- trigger: the annotation display -->
    <template #toggle>
      <div class="fts-ai-annotation fts-ai-annotation--editable" title="Click to edit AI annotation">
        <i class="fts-icon-magic-lamp inistate-text-ai fts-ai-annotation-icon" />
        <div class="fts-ai-annotation-body inistate-text-ai">
          <div v-for="(line, index) in lines" :key="index" class="fts-ai-annotation-line">
            <font-awesome-icon :icon="line.icon" class="fts-ai-annotation-line-icon" fixed-width />{{ line.text }}
          </div>
        </div>
        <font-awesome-icon :icon="['fas', 'pen']" class="fts-ai-annotation-edit-hint" fixed-width />
      </div>
    </template>
    <!-- content: the edit form -->
    <template #menu="{ select }">
      <div class="fts-ai-annotation-edit">
        <div class="fts-ai-annotation-edit-title">
          <i class="fts-icon-magic-lamp inistate-text-ai" /> Edit AI annotation
        </div>
        <div class="fts-ai-annotation-edit-field" v-if="fields.includes('hint')">
          <label>
            <font-awesome-icon :icon="['fas', 'lightbulb']" fixed-width /> Hint
          </label>
          <textarea
            v-model="draft.hint"
            rows="2"
            placeholder="A short hint for the AI"
          />
        </div>
        <div class="fts-ai-annotation-edit-field" v-if="fields.includes('actor')">
          <label>
            <font-awesome-icon :icon="['fas', 'user']" fixed-width /> Actor
          </label>
          <select v-model="draft.actor">
            <option v-for="option in actorOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="fts-ai-annotation-edit-field" v-if="fields.includes('instruction')">
          <label>
            <font-awesome-icon :icon="['fas', 'tasks']" fixed-width /> Instruction
          </label>
          <textarea
            v-model="draft.instruction"
            rows="2"
            placeholder="How the AI should perform this"
          />
        </div>
        <div class="fts-ai-annotation-edit-field" v-if="fields.includes('confidence')">
          <label>
            <font-awesome-icon :icon="['fas', 'exclamation-circle']" fixed-width /> Confidence threshold (%)
          </label>
          <input
            v-model="draft.confidence"
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="e.g. 80"
          />
        </div>
        <div class="fts-ai-annotation-edit-actions">
          <fts-btn sm text text-transform-init @click="select()">Cancel</fts-btn>
          <fts-btn sm color="ai" box-shadow-none text-transform-init @click="onSave(select)">OK</fts-btn>
        </div>
      </div>
    </template>
  </fts-dropdown-select>
  <!-- read-only display -->
  <div class="fts-ai-annotation" v-else-if="hasContent">
    <!-- AI accent icon -->
    <i class="fts-icon-magic-lamp inistate-text-ai fts-ai-annotation-icon" />
    <!-- annotation lines (gradient text via inistate-text-ai) -->
    <div class="fts-ai-annotation-body inistate-text-ai">
      <div v-for="(line, index) in lines" :key="index" class="fts-ai-annotation-line">
        <font-awesome-icon :icon="line.icon" class="fts-ai-annotation-line-icon" fixed-width />{{ line.text }}
      </div>
    </div>
  </div>
</template>

<script>
import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSBtn from "../elements/FTSBtn";

/**
 * Renders the AI design annotations (actor, instruction, hint, confidence) for a
 * schema element as a compact, subtle block. Any field left empty is omitted, and
 * the whole block renders nothing when none are provided.
 *
 * When `editable` is set, clicking the block opens an inline popover to edit the
 * instruction, hint and confidence threshold, emitting a `save` event with the
 * updated values. `confidence` is emitted as a 0-1 fraction to match the schema.
 */
export default {
  name: "FTSAiAnnotation",
  components: {
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-btn": FTSBtn,
  },
  props: {
    instruction: { type: String, default: "" },
    hint: { type: String, default: "" },
    actor: { type: String, default: "" },
    // confidence threshold as a 0-1 fraction (0.8) or an already-scaled percentage (80)
    confidence: { type: [Number, String], default: null },
    // when true, the annotation becomes a click-to-edit popover (emits `save`)
    editable: { type: Boolean, default: false },
    // which fields the edit popover exposes, in render order
    fields: {
      type: Array,
      default: () => ["hint", "actor", "instruction", "confidence"],
    },
    // optional element id used to constrain the edit popover within a scroll container
    boundary: { type: String, default: undefined },
  },
  data: () => ({
    draft: {
      instruction: "",
      hint: "",
      confidence: "",
      actor: "",
    },
    actorOptions: [
      { value: "", label: "—" },
      { value: "human", label: "Human" },
      { value: "ai", label: "AI" },
      { value: "hybrid", label: "Hybrid" },
    ],
  }),
  computed: {
    confidencePercent() {
      const value = typeof this.confidence === "string" ? parseFloat(this.confidence) : this.confidence;
      if (typeof value !== "number" || isNaN(value) || value <= 0) return null;
      return value <= 1 ? Math.round(value * 100) : Math.round(value);
    },
    hasContent() {
      return !!(this.instruction || this.hint || this.actor || this.confidencePercent !== null);
    },
    lines() {
      const lines = [];
      if (this.actor) lines.push({ icon: ["fas", "user"], text: this.actor });
      if (this.instruction) lines.push({ icon: ["fas", "tasks"], text: this.instruction });
      if (this.hint) lines.push({ icon: ["fas", "lightbulb"], text: this.hint });
      if (this.confidencePercent !== null) {
        lines.push({ icon: ["fas", "exclamation-circle"], text: `Confidence ≥ ${this.confidencePercent}%` });
      }
      return lines;
    },
  },
  methods: {
    // seed the form from the current values whenever the popover opens
    initDraft() {
      this.draft.instruction = this.instruction || "";
      this.draft.hint = this.hint || "";
      this.draft.actor = this.actor || "";
      this.draft.confidence = this.confidencePercent === null ? "" : String(this.confidencePercent);
    },
    onSave(close) {
      // only emit the fields this annotation actually exposes, so hidden fields
      // are never overwritten on save
      const payload = {};
      if (this.fields.includes("hint")) payload.hint = this.draft.hint.trim();
      if (this.fields.includes("actor")) payload.actor = this.draft.actor;
      if (this.fields.includes("instruction")) payload.instruction = this.draft.instruction.trim();
      if (this.fields.includes("confidence")) {
        const percent = parseFloat(this.draft.confidence);
        payload.confidence = !isNaN(percent) && percent > 0 ? Math.min(100, percent) / 100 : 0;
      }
      this.$emit("save", payload);
      if (typeof close === "function") close();
    },
  },
};
</script>

<style lang="scss" scoped>
.fts-ai-annotation {
  display: flex;
  align-items: flex-start;
  margin-top: 0.1875rem;
  font-size: 0.6875rem;
  line-height: 1.25;
  // AI icon stays in front; the gradient marks it as the accent
  .fts-ai-annotation-icon {
    flex-shrink: 0;
    margin-top: 0.05rem;
    margin-right: 0.3125rem;
    font-size: 0.625rem;
  }
  .fts-ai-annotation-body {
    flex: 1;
    min-width: 0;
  }
  .fts-ai-annotation-line {
    word-break: break-word;
    // line icons align in a left column, smaller and a soft purple, lighter than the AI accent
    .fts-ai-annotation-line-icon {
      margin-right: 0.3125rem;
      font-size: 0.625rem;
      color: rgba(var(--fts-ai-rgb), 0.6);
      -webkit-text-fill-color: rgba(var(--fts-ai-rgb), 0.6);
    }
  }
  .fts-ai-annotation-line + .fts-ai-annotation-line {
    margin-top: 0.0625rem;
  }
  // editable affordance: a subtle pencil that appears on hover
  &.fts-ai-annotation--editable {
    cursor: pointer;
    border-radius: 0.25rem;
    .fts-ai-annotation-edit-hint {
      flex-shrink: 0;
      margin-top: 0.05rem;
      margin-left: 0.3125rem;
      font-size: 0.5625rem;
      color: rgba(var(--fts-ai-rgb), 0.6);
      visibility: hidden;
    }
    &:hover {
      background-color: rgba(var(--fts-ai-rgb), 0.08);
      .fts-ai-annotation-edit-hint {
        visibility: visible;
      }
    }
  }
}

// edit popover
.fts-ai-annotation-edit {
  width: 100%;
  .fts-ai-annotation-edit-title {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--fts-text);
  }
  .fts-ai-annotation-edit-field {
    margin-bottom: 0.625rem;
    label {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: 0.25rem;
      font-size: 0.75rem;
      color: var(--fts-default);
    }
    textarea,
    input,
    select {
      width: 100%;
      padding: 0.375rem 0.5rem;
      border: 1px solid var(--fts-border);
      border-radius: 0.25rem;
      background-color: var(--fts-card-background);
      color: var(--fts-text);
      // form controls don't inherit the page font — pin it to the design system
      font-family: var(--fts-font-family);
      font-size: 0.8125rem;
      line-height: 1.4;
      &:focus {
        outline: none;
        border-color: var(--fts-primary);
      }
    }
    textarea {
      resize: vertical;
    }
    select {
      height: 2rem;
      cursor: pointer;
    }
  }
  .fts-ai-annotation-edit-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }
}
</style>

<style lang="scss">
/**
 * The edit popover content is rendered by the child dropdown component, so it sits
 * outside this component's scoped styles. We give it a unique class via the
 * dropdown's `content-class` (which REPLACES the shared dropdown classes rather
 * than appending), so this rule is fully self-contained and cannot affect any
 * other dropdown/popover in the app. The visual chrome (background, shadow,
 * radius, vertical padding) still comes from the base `.popper-content` class.
 */
.fts-ai-annotation-edit-content {
  width: 19rem;
  max-width: calc(100vw - 2rem);
  box-sizing: border-box;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
</style>
